import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';
import { LogoIcon } from './Logo';

interface PrivacyPolicyProps {
  onBack?: () => void;
}

export function PrivacyPolicy({ onBack }: PrivacyPolicyProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {onBack && (
          <Button
            variant="ghost"
            onClick={onBack}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}

        <Card className="border-2 border-emerald-100 shadow-xl">
          <CardHeader className="text-center space-y-4 pb-6">
            <div className="flex justify-center mb-2">
              <LogoIcon size="lg" />
            </div>
            <CardTitle className="text-3xl">Privacy Policy</CardTitle>
            <p className="text-sm text-muted-foreground">
              Last Updated: November 30, 2025
            </p>
          </CardHeader>

          <CardContent className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">1. Introduction</h2>
              <p className="text-muted-foreground">
                Better to Give than Receive ("we", "our", "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you use our collaborative gift-giving platform.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">2. Information We Collect</h2>
              <p className="text-muted-foreground mb-2">
                <strong>2.1 Personal Information:</strong> When you create an account, we collect:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Email address and/or phone number (for authentication)</li>
                <li>Name (optional, for personalization)</li>
                <li>Birthdate (optional, for gift group features)</li>
                <li>Shipping addresses (when participating in gift groups)</li>
              </ul>
              <p className="text-muted-foreground mt-3 mb-2">
                <strong>2.2 Transaction Information:</strong> We collect:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Gift group participation history</li>
                <li>Contribution amounts and payment records</li>
                <li>Gift suggestions and rankings</li>
              </ul>
              <p className="text-muted-foreground mt-3 mb-2">
                <strong>2.3 Usage Data:</strong> We automatically collect:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>Browser type and device information</li>
                <li>IP address and general location</li>
                <li>Pages visited and actions taken on our platform</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">3. How We Use Your Information</h2>
              <p className="text-muted-foreground">We use your information to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
                <li>Provide and maintain our gift-giving platform</li>
                <li>Process payments and fulfill gift orders</li>
                <li>Send transactional emails and SMS messages about your gift groups</li>
                <li>Improve our services and user experience</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">4. Information Sharing</h2>
              <p className="text-muted-foreground mb-2">
                <strong>4.1 With Gift Group Members:</strong> When you participate in a gift group, 
                other gifters may see your name and contribution status. Giftees do NOT have access 
                to gift group details, contributor information, or gift suggestions.
              </p>
              <p className="text-muted-foreground mb-2">
                <strong>4.2 With Service Providers:</strong> We share information with third parties 
                who help us operate our platform:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1">
                <li>PayPal (for payment processing)</li>
                <li>MessageBird (for email and SMS delivery)</li>
                <li>Amazon and other retailers (for gift fulfillment)</li>
                <li>Cloudflare (for hosting and security)</li>
              </ul>
              <p className="text-muted-foreground mt-3">
                <strong>4.3 Legal Requirements:</strong> We may disclose your information if required 
                by law, court order, or government request.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">5. Data Security</h2>
              <p className="text-muted-foreground">
                We implement industry-standard security measures to protect your personal information:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
                <li>All data is encrypted in transit using HTTPS/TLS</li>
                <li>Passwords are never stored; we use magic link authentication</li>
                <li>Payment information is processed by PayPal and never stored on our servers</li>
                <li>Access to personal data is restricted to authorized personnel only</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">6. Your Rights</h2>
              <p className="text-muted-foreground">You have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate or incomplete data</li>
                <li>Delete your account and associated data</li>
                <li>Opt-out of marketing communications</li>
                <li>Request a copy of your data in a portable format</li>
              </ul>
              <p className="text-muted-foreground mt-3">
                To exercise these rights, please contact us at{' '}
                <a href="mailto:support@btgtr.com" className="text-emerald-600 hover:text-emerald-700 underline">
                  support@btgtr.com
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">7. Cookies and Tracking</h2>
              <p className="text-muted-foreground">
                We use essential cookies to maintain your login session and remember your preferences. 
                We may also use analytics tools to understand how our platform is used. You can manage 
                cookie preferences in your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">8. Children's Privacy</h2>
              <p className="text-muted-foreground">
                Our platform is not intended for users under 18 years of age. We do not knowingly 
                collect personal information from children. If you believe we have collected information 
                from a child, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">9. Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any material 
                changes by posting the new policy on this page and updating the "Last Updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-emerald-700 mb-3">10. Contact Us</h2>
              <p className="text-muted-foreground">
                If you have questions about this Privacy Policy or our data practices, please contact us at:{' '}
                <a href="mailto:support@btgtr.com" className="text-emerald-600 hover:text-emerald-700 underline">
                  support@btgtr.com
                </a>
              </p>
            </section>

            <div className="pt-6 mt-8 border-t border-gray-200">
              <p className="text-xs text-center text-muted-foreground italic">
                By using Better to Give than Receive, you acknowledge that you have read and understood 
                this Privacy Policy.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
